import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import {TEXT_MSGS,PREMIUM_UPGRADE_MSGS_LIST,ELITE_UPGRADE_MSGS_LIST} from '../../../common/utility/constant';
import { useNavigate } from "react-router-dom";
// ============================================================
// NEW - Centralized Permission System
// See: src/common/hooks/usePermissions.js
// NOTE: Phase 5 - Upgrade messaging migration
// Currently uses hardcoded messages, should use permissions.getUpgradeMessage(feature)
// ============================================================
import usePermissions from '../../../common/hooks/usePermissions';
import {
  captureChartsPaywallShown,
  captureChartsUpgradeClicked,
} from '../../../analytics/chartsFunnel';

export default function SubscriptionPopup(props) {
  const navigate = useNavigate();
  // ============================================================
  // NEW - Centralized Permission System
  // See: src/common/utility/permissionService.js
  // NOTE: Phase 5 - Upgrade messaging migration
  // Currently uses hardcoded messages, should use permissions.getUpgradeMessage(feature)
  // ============================================================
  const permissions = usePermissions();
  
  const [open, setOpen] = React.useState(props.open);
  const [msgIndex,setMsgIndex]=React.useState(0);
  const [loaded,setLoaded]=React.useState(false);
  const isPremiumUser = permissions.isPremiumUser();
  
  const handleClose = () => {
    setOpen(false)
    props.onClose();
  }

  React.useEffect(()=>{
    let val=JSON.parse(localStorage.getItem('upgrade_msg_index'));
    let index=0;
    let count=0;
    
    // Set count based on user tier (needed for rotation logic)
    if(isPremiumUser){
      count=ELITE_UPGRADE_MSGS_LIST.length
    }
    else{
      count=PREMIUM_UPGRADE_MSGS_LIST.length
    }
    
    if(val !== null && val !== undefined){
      index=parseInt(val);
    }
    else{
      // First time - start with random message
      index=Math.floor(Math.random()*count);
    }
      
    // Rotate to next message
    if(index<count-1)
      index=index+1
    else 
      index=0;
      
    setMsgIndex(index);
    setLoaded(true);
    localStorage.setItem("upgrade_msg_index",JSON.stringify(index));
  },[])

  React.useEffect(() => {
    if (!open) {
      return;
    }
    captureChartsPaywallShown({
      paywall_trigger: props.paywallTrigger || 'feature_gate',
      upgrade_target: props.proMessage ? 'elite' : 'professional',
    });
  }, [open, props.paywallTrigger, props.proMessage]);

  const openSubscription=()=>{
    captureChartsUpgradeClicked({
      cta_surface: 'paywall_modal',
      upgrade_target: props.proMessage ? 'elite' : 'professional',
    });
    props.onClose();
    navigate('/subscription')
  }
    
  // Get the message to display
  const getMessage = () => {
    if (props.data && props.data.isCustom) {
      return props.data.msg;
    }
    if (props.proMessage !== undefined && props.proMessage) {
      return TEXT_MSGS.PREMIUM_PLUS_UPGRADE_MSG;
    }
    if (loaded) {
      return isPremiumUser ? ELITE_UPGRADE_MSGS_LIST[msgIndex] : PREMIUM_UPGRADE_MSGS_LIST[msgIndex];
    }
    return '';
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(30, 60, 114, 0.6)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.4s ease',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 }
          }
        }
      }}
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '500px' },
        maxWidth: '500px',
        minWidth: '450px',
        bgcolor: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
        padding: '30px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        animation: 'slideIn 0.5s ease',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translate(-50%, -50%) translateY(-30px) scale(0.9)'
          },
          to: {
            opacity: 1,
            transform: 'translate(-50%, -50%) translateY(0) scale(1)'
          }
        }
      }}>
        {/* Title: "Upgrade now!" (centered, bold) */}
        <Typography 
          id="modal-modal-title" 
          variant="h4" 
          component="h2"
          sx={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#333333',
            marginBottom: '15px',
            textAlign: 'center',
            lineHeight: '1.2'
          }}
        >
          Upgrade now!
        </Typography>

        {/* Message box: light blue gradient with left border, contains the rotating message */}
        <Box sx={{
          background: 'linear-gradient(135deg, #f8f9ff, #e8f0ff)',
          borderLeft: '4px solid #667eea',
          padding: '20px',
          borderRadius: '10px',
          margin: '20px 0'
        }}>
          <Typography 
            id="modal-modal-description"
            sx={{
              color: '#555555',
              lineHeight: '1.6',
              fontSize: '14px'
            }}
          >
            {getMessage()}
          </Typography>
        </Box>

        {/* Two buttons */}
        <Box sx={{
          marginTop: '30px',
          display: 'flex',
          gap: '15px',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center'
        }}>
          {/* "Upgrade Now" — gradient purple-to-blue, routes to /subscription */}
          <Button
            variant="contained"
            onClick={openSubscription}
            sx={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#ffffff',
              padding: '12px 25px',
              border: 'none',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              flex: 1,
              fontSize: '14px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                background: 'linear-gradient(135deg, #5a6fd8, #6a4190)'
              },
              '&:active': {
                transform: 'translateY(0)',
                transition: 'all 0.1s ease'
              }
            }}
          >
            Upgrade Now
          </Button>

          {/* "Not Now" — outlined, closes the popup */}
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              background: 'transparent',
              color: '#667eea',
              border: '2px solid #667eea',
              padding: '12px 25px',
              borderRadius: '25px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              flex: 1,
              fontSize: '14px',
              '&:hover': {
                background: '#667eea',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
              },
              '&:active': {
                transform: 'translateY(0)',
                transition: 'all 0.1s ease'
              }
            }}
          >
            Not Now
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}